import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { gql, useApolloClient, useMutation, useQuery } from '@apollo/client';
import { ChevronLeft, ChevronRight, FileText, RefreshCw } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { UnifiedTable, UnifiedTableColumn } from '@/components/ui/unifiedTable/UnifiedTable';
import './caseConferencing.css';
import { CaseConferencingDetailsDialog } from './CaseConferencingDetailsDialog';
import { CaseConferencingFilterPicker } from './CaseConferencingFilterPicker';

type SortDirection = 'asc' | 'desc';
type PriorityOption = 0 | 1;

interface SortConfig {
  key: string | null;
  direction: SortDirection | null;
}

interface FilterState {
  program: string[];
  facility: string[];
  provider: string[];
  priority: string;
  firstname: string;
  lastname: string;
  cid: string;
}

interface CaseConferencingRow {
  id: number | null;
  participant: number;
  odrparticipant: number | null;
  updated_at: string | null;
  program: string;
  program_for_link?: string | null;
  dob: string | null;
  firstname: string;
  lastname: string;
  bookingnumber: string | number | null;
  provider: string | null;
  cid: string | number | null;
  cin: string | number | null;
  facility: string | null;
  hearingdate: string | null;
  releasedon: string | null;
  maxdate: string | null;
  psh_search_status: string | null;
  adherence_rate?: number | null;
  priority: string | null;
  priority_label: string | null;
  priority_id: number | null;
  lcm: string | null;
}

const CASE_CONFERENCING_DASHBOARD = gql`
  query CaseConferencingDashboard(
    $page: Int
    $pageSize: Int
    $sortKey: String
    $sortDirection: String
    $forceParticipant: Int
    $filters: CaseConferencingFiltersInput
  ) {
    caseConferencingDashboard(
      page: $page
      pageSize: $pageSize
      sortKey: $sortKey
      sortDirection: $sortDirection
      forceParticipant: $forceParticipant
      filters: $filters
    ) {
      rows {
        id
        participant
        odrparticipant
        updated_at
        program
        program_for_link
        dob
        firstname
        lastname
        bookingnumber
        provider
        cid
        cin
        facility
        hearingdate
        releasedon
        maxdate
        psh_search_status
        adherence_rate
        priority
        priority_label
        priority_id
        lcm
      }
      facilities
      providers
      items
      pages
      page
      pageSize
    }
  }
`;

const CASE_CONFERENCING_REPORT_ROWS = gql`
  query CaseConferencingReportRows(
    $sortKey: String
    $sortDirection: String
    $forceParticipant: Int
    $filters: CaseConferencingFiltersInput
    $limit: Int
  ) {
    caseConferencingReportRows(
      sortKey: $sortKey
      sortDirection: $sortDirection
      forceParticipant: $forceParticipant
      filters: $filters
      limit: $limit
    ) {
      id
      participant
      program
      dob
      firstname
      lastname
      bookingnumber
      provider
      cid
      cin
      facility
      hearingdate
      releasedon
      maxdate
      psh_search_status
      adherence_rate
      priority_label
      lcm
    }
  }
`;

const UPDATE_CASE_CONFERENCING_PRIORITY = gql`
  mutation UpdateCaseConferencingPriority($participant: Int!, $priorityId: Int!) {
    updateCaseConferencingPriority(participant: $participant, priorityId: $priorityId) {
      success
      participant
      field
      value
    }
  }
`;

const DEFAULT_PAGE_SIZE = 100;

const DEFAULT_FILTERS: FilterState = {
  program: [],
  facility: [],
  provider: [],
  priority: '',
  firstname: '',
  lastname: '',
  cid: '',
};

type ListFilterKey = 'program' | 'provider' | 'facility';
type TextFilterKey = 'priority' | 'firstname' | 'lastname' | 'cid';
const LIST_FILTER_KEYS: ListFilterKey[] = ['program', 'provider', 'facility'];
const LIST_FILTER_URL_KEYS: Record<ListFilterKey, string> = { program: 'programs', provider: 'providers', facility: 'facilities' };
const TEXT_FILTER_KEYS: TextFilterKey[] = ['priority', 'firstname', 'lastname', 'cid'];

const toServerFilters = (filters: FilterState) => ({
  program: filters.program.length === 1 ? filters.program[0] : null,
  programs: filters.program.length > 1 ? filters.program : null,
  provider: filters.provider.length === 1 ? filters.provider[0] : null,
  providers: filters.provider.length > 1 ? filters.provider : null,
  facility: filters.facility.length === 1 ? filters.facility[0] : null,
  facilities: filters.facility.length > 1 ? filters.facility : null,
  priority: filters.priority || null,
  firstname: filters.firstname || null,
  lastname: filters.lastname || null,
  cid: filters.cid || null,
});

const PROGRAM_LABELS: Record<string, string> = {
  zodr: 'ODR Housing',
  mist: 'MIST',
  lead: 'LEAD',
  prg_wmn: 'Pregnant Woman',
  dsh: 'DSH',
  fist: 'FIST',
};

const getCaseConferencingRowKey = (row: CaseConferencingRow, index: number) => [
  row.id ?? 'no-id',
  row.participant,
  row.program || 'no-program',
  row.bookingnumber ?? 'no-booking',
  index,
].join(':');

const PRIORITY_OPTIONS: Array<{ id: PriorityOption; label: string }> = [
  { id: 1, label: 'Yes' },
  { id: 0, label: 'No' },
];

const normalizeSortKeyForServer = (sortKey: string | null): string | null => {
  if (!sortKey) {
    return null;
  }

  if (sortKey === 'firstName') {
    return 'firstname';
  }

  if (sortKey === 'lastName') {
    return 'lastname';
  }

  return sortKey;
};

const toCsvValue = (value: unknown): string => {
  if (value === null || value === undefined) {
    return '';
  }

  return String(value).replace(/"/g, '""');
};

const downloadCsv = (fileName: string, csv: string) => {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = window.URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  try {
    anchor.click();
  } finally {
    anchor.remove();
    window.URL.revokeObjectURL(url);
  }
};

const PriorityEditor = ({
  value,
  onChange,
  disabled,
}: {
  value: number | null;
  onChange: (next: PriorityOption) => void;
  disabled: boolean;
}) => {
  const [open, setOpen] = useState(false);

  const selectedValue: PriorityOption = value === 1 ? 1 : 0;

  return (
    <div className="cc-priority-cell">
      <button
        type="button"
        disabled={disabled}
        className={`cc-priority-badge ${selectedValue === 1 ? 'cc-priority-yes' : 'cc-priority-no'}`}
        onClick={(event) => {
          event.stopPropagation();
          setOpen((prev) => !prev);
        }}
      >
        {selectedValue === 1 ? 'Yes' : 'No'}
      </button>

      {open && (
        <div
          className="cc-priority-popover"
          onClick={(event) => {
            event.stopPropagation();
          }}
        >
          <div className="cc-priority-options">
            {PRIORITY_OPTIONS.map((option) => (
              <label key={option.id} className="cc-priority-option">
                <input
                  type="radio"
                  disabled={disabled}
                  name="priority"
                  checked={selectedValue === option.id}
                  onChange={() => {
                    onChange(option.id);
                    setOpen(false);
                  }}
                />
                <span>{option.label}</span>
              </label>
            ))}
          </div>
          <div className="cc-popover-footer">
            <button
              type="button"
              className="cc-button cc-button-neutral"
              onClick={() => setOpen(false)}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const DateEditor = ({
  value,
  onSave,
}: {
  value: string | null;
  onSave: (date: string) => void;
}) => {
  const [open, setOpen] = useState(false);
  const [draftValue, setDraftValue] = useState(value || '');

  useEffect(() => {
    setDraftValue(value || '');
  }, [value]);

  return (
    <div className="cc-date-cell">
      <button
        type="button"
        className="cc-date-display"
        onClick={(event) => {
          event.stopPropagation();
          setOpen((prev) => !prev);
        }}
      >
        {value || '-'}
      </button>

      {open && (
        <div
          className="cc-date-popover"
          onClick={(event) => {
            event.stopPropagation();
          }}
        >
          <input
            className="cc-date-input"
            type="date"
            value={draftValue}
            onChange={(event) => setDraftValue(event.target.value)}
          />
          <div className="cc-popover-footer">
            <button
              type="button"
              className="cc-button cc-button-neutral"
              onClick={() => {
                setDraftValue(value || '');
                setOpen(false);
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              className="cc-button cc-button-primary"
              onClick={() => {
                if (!draftValue) {
                  setOpen(false);
                  return;
                }

                onSave(draftValue);
                setOpen(false);
              }}
            >
              Save
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export const CaseConferencing = () => {
  const apolloClient = useApolloClient();

  const [error, setError] = useState<string | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [isUpdatingPriority, setIsUpdatingPriority] = useState(false);
  const [detailParticipant, setDetailParticipant] = useState<CaseConferencingRow | null>(null);

  const [filters, setFilters] = useState<FilterState>(DEFAULT_FILTERS);
  const [debouncedFilters, setDebouncedFilters] = useState<FilterState>(DEFAULT_FILTERS);

  const [currentPage, setCurrentPage] = useState(1);

  const [sortConfig, setSortConfig] = useState<SortConfig>({
    key: null,
    direction: null,
  });

  const [forceParticipant, setForceParticipant] = useState<string | null>(null);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedFilters(filters);
    }, 120);

    return () => window.clearTimeout(timer);
  }, [filters]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);

    const nextFilters: FilterState = { ...DEFAULT_FILTERS };

    LIST_FILTER_KEYS.forEach((key) => {
      const explicitValues = params.getAll(LIST_FILTER_URL_KEYS[key]);
      nextFilters[key] = explicitValues.length > 0
        ? explicitValues
        : (params.get(key) || '').split(',').map((value) => value.trim()).filter(Boolean);
    });
    TEXT_FILTER_KEYS.forEach((key) => {
      nextFilters[key] = params.get(key) || '';
    });

    setFilters(nextFilters);
    setDebouncedFilters(nextFilters);

    const sortKey = params.get('sortKey');
    const sortDirection = params.get('sortDirection');

    if (sortKey && (sortDirection === 'asc' || sortDirection === 'desc')) {
      setSortConfig({ key: sortKey, direction: sortDirection });
    }

    const pageParam = Number.parseInt(String(params.get('page') || '1'), 10);
    if (Number.isFinite(pageParam) && pageParam > 0) {
      setCurrentPage(pageParam);
    }

    setForceParticipant(params.get('force-participant'));
  }, []);

  useEffect(() => {
    const url = new URL(window.location.href);

    if (sortConfig.key && sortConfig.direction) {
      url.searchParams.set('sortKey', sortConfig.key);
      url.searchParams.set('sortDirection', sortConfig.direction);
    }

    if (!sortConfig.key || !sortConfig.direction) {
      url.searchParams.delete('sortKey');
      url.searchParams.delete('sortDirection');
    }

    url.searchParams.set('page', String(currentPage));
    window.history.replaceState({}, '', url.toString());
  }, [sortConfig.direction, sortConfig.key, currentPage]);

  const dashboardVariables = useMemo(() => {
    const forceParticipantInt = Number.parseInt(String(forceParticipant || ''), 10);

    return {
      page: currentPage,
      pageSize: DEFAULT_PAGE_SIZE,
      sortKey: normalizeSortKeyForServer(sortConfig.key),
      sortDirection: sortConfig.direction,
      forceParticipant: Number.isFinite(forceParticipantInt) ? forceParticipantInt : null,
      filters: toServerFilters(debouncedFilters),
    };
  }, [currentPage, debouncedFilters, forceParticipant, sortConfig.direction, sortConfig.key]);

  const { data, loading, error: queryError, refetch } = useQuery(CASE_CONFERENCING_DASHBOARD, {
    variables: dashboardVariables,
    fetchPolicy: 'network-only',
    pollInterval: 30000,
  });

  const [runUpdatePriority] = useMutation(UPDATE_CASE_CONFERENCING_PRIORITY);

  const rows = useMemo<CaseConferencingRow[]>(() => {
    const incoming = data?.caseConferencingDashboard?.rows;
    if (!Array.isArray(incoming)) {
      return [];
    }

    return incoming as CaseConferencingRow[];
  }, [data?.caseConferencingDashboard?.rows]);

  const pages = useMemo(() => {
    const rawPages = Number.parseInt(String(data?.caseConferencingDashboard?.pages ?? 1), 10);
    if (!Number.isFinite(rawPages) || rawPages < 1) {
      return 1;
    }

    return rawPages;
  }, [data?.caseConferencingDashboard?.pages]);

  useEffect(() => {
    if (loading || !data?.caseConferencingDashboard) {
      return;
    }

    if (currentPage <= pages) {
      return;
    }

    setCurrentPage(pages);
  }, [currentPage, data?.caseConferencingDashboard, loading, pages]);

  const facilities = useMemo(() => {
    const incoming = data?.caseConferencingDashboard?.facilities;
    if (!Array.isArray(incoming)) {
      return [] as string[];
    }

    return incoming as string[];
  }, [data?.caseConferencingDashboard?.facilities]);

  const providers = useMemo(() => {
    const incoming = data?.caseConferencingDashboard?.providers;
    if (!Array.isArray(incoming)) {
      return [] as string[];
    }

    return incoming as string[];
  }, [data?.caseConferencingDashboard?.providers]);

  const providerOptions = useMemo(() => {
    const values = new Set<string>();

    providers.forEach((provider) => {
      const nextValue = String(provider || '').trim();
      if (!nextValue) {
        return;
      }

      values.add(nextValue);
    });

    rows.forEach((row) => {
      const nextValue = String(row.provider || '').trim();
      if (!nextValue) {
        return;
      }

      values.add(nextValue);
    });

    return Array.from(values).sort((left, right) => left.localeCompare(right));
  }, [providers, rows]);

  const facilityOptions = useMemo(() => {
    const values = new Set<string>();

    facilities.forEach((facility) => {
      const nextValue = String(facility || '').trim();
      if (!nextValue) {
        return;
      }

      values.add(nextValue);
    });

    rows.forEach((row) => {
      const nextValue = String(row.facility || '').trim();
      if (!nextValue) {
        return;
      }

      values.add(nextValue);
    });

    return Array.from(values).sort((left, right) => left.localeCompare(right));
  }, [facilities, rows]);

  const updateUrlParam = (key: string, value: string) => {
    const url = new URL(window.location.href);

    if (value) {
      url.searchParams.set(key, value);
    }

    if (!value) {
      url.searchParams.delete(key);
    }

    window.history.replaceState({}, '', url.toString());
  };

  const clearFilters = () => {
    setFilters(DEFAULT_FILTERS);
    setDebouncedFilters(DEFAULT_FILTERS);
    setSortConfig({ key: null, direction: null });
    setForceParticipant(null);
    setCurrentPage(1);

    const url = new URL(window.location.href);
    url.search = '';
    window.history.replaceState({}, '', url.toString());
  };

  const handleFilterChange = (filterKey: TextFilterKey, nextValue: string) => {
    setCurrentPage(1);
    setFilters((prev) => ({ ...prev, [filterKey]: nextValue }));
    updateUrlParam(filterKey, nextValue);
  };

  const handleListFilterChange = (filterKey: ListFilterKey, nextValue: string[]) => {
    setCurrentPage(1);
    setFilters((previous) => ({ ...previous, [filterKey]: nextValue }));
    const url = new URL(window.location.href);
    const listKey = LIST_FILTER_URL_KEYS[filterKey];
    url.searchParams.delete(filterKey);
    url.searchParams.delete(listKey);
    if (nextValue.some((value) => value.includes(','))) {
      nextValue.forEach((value) => url.searchParams.append(listKey, value));
      window.history.replaceState({}, '', url.toString());
      return;
    }
    if (nextValue.length > 0) url.searchParams.set(filterKey, nextValue.join(','));
    window.history.replaceState({}, '', url.toString());
  };

  const handleSortChange = (key: string, direction: SortDirection) => {
    setSortConfig({ key, direction });
  };

  const onPriorityEdit = useCallback(
    async (participant: number, priorityId: PriorityOption) => {
      if (isUpdatingPriority) return;
      setIsUpdatingPriority(true);
      try {
        const result = await runUpdatePriority({
          variables: {
            participant,
            priorityId,
          },
        });
        if (result.errors?.length || result.data?.updateCaseConferencingPriority?.success !== true) {
          throw new Error('Priority update was not confirmed');
        }

        try {
          await refetch();
        } catch {
          setError('Priority was updated, but the list could not be refreshed. Refresh the page.');
          return;
        }
        setError(null);
      } catch {
        setError('Unable to update priority. Please try again.');
      } finally {
        setIsUpdatingPriority(false);
      }
    },
    [isUpdatingPriority, refetch, runUpdatePriority],
  );

  const refreshDashboard = async () => {
    try {
      await refetch();
      setError(null);
    } catch {
      setError('Unable to refresh case conferencing data. Please try again.');
    }
  };

  const handleExportCsv = async () => {
    if (isExporting) {
      return;
    }

    setIsExporting(true);

    try {
      const forceParticipantInt = Number.parseInt(String(forceParticipant || ''), 10);
      const reportResult = await apolloClient.query<{ caseConferencingReportRows: CaseConferencingRow[] }>({
        query: CASE_CONFERENCING_REPORT_ROWS,
        variables: {
          sortKey: normalizeSortKeyForServer(sortConfig.key),
          sortDirection: sortConfig.direction,
          forceParticipant: Number.isFinite(forceParticipantInt) ? forceParticipantInt : null,
          filters: toServerFilters(debouncedFilters),
          limit: 50000,
        },
        fetchPolicy: 'network-only',
      });

      if (reportResult.errors?.length || !Array.isArray(reportResult.data?.caseConferencingReportRows)) {
        throw new Error('Report result is unavailable');
      }
      const reportRows = reportResult.data.caseConferencingReportRows;

      const headers = [
        'Firstname',
        'Lastname',
        'DOB',
        'Booking',
        'CID',
        'CIN',
        'Program',
        'Facility',
        'Provider',
        'Next Hearing Date',
        'Intake/Release Date',
        'Max Date',
        'PSH Search Status',
        'ECM LCM',
        'Adherence Rate',
        'Priority',
      ];

      const csvRows = reportRows.map((row) => {
        const adherence = typeof row.adherence_rate === 'number' ? `${row.adherence_rate}` : '';
        const programLabel = PROGRAM_LABELS[row.program] || row.program || '';

        return [
          row.firstname || '',
          row.lastname || '',
          row.dob || '',
          row.bookingnumber || '',
          row.cid || 'No CID',
          row.cin || '',
          programLabel,
          row.facility || '',
          row.provider || '',
          row.hearingdate || '',
          row.releasedon || '',
          row.maxdate || '',
          row.psh_search_status || 'Status Not Set',
          row.lcm || '',
          adherence,
          row.priority_label || 'No',
        ]
          .map((value) => `"${toCsvValue(value)}"`)
          .join(',');
      });

      const csv = [`"${headers.join('","')}"`, ...csvRows].join('\n');
      const fileName = `case-conferencing-report-${new Date().toISOString().slice(0, 10)}.csv`;
      downloadCsv(fileName, csv);
      setError(null);
    } catch {
      setError('Unable to export report. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  const hasActiveFilters = Object.values(filters).some((value) => Array.isArray(value) ? value.length > 0 : value.trim() !== '');
  const toolbarActionButtonClass =
    '[&_button]:h-8 [&_button]:text-xs';
  const toolbarActionRowClass = `flex w-full flex-wrap items-center justify-start gap-2 ${toolbarActionButtonClass}`;

  const effectiveError = error || (queryError ? 'Unable to load case conferencing data. Please try again.' : null);

  const columns = useMemo<UnifiedTableColumn<CaseConferencingRow>[]>(() => {
    return [
      { key: 'firstName', header: 'Firstname', accessor: (row) => row.firstname || '-', searchValue: (row) => row.firstname, sortable: true, width: '140px' },
      { key: 'lastName', header: 'Lastname', accessor: (row) => row.lastname || '-', searchValue: (row) => row.lastname, sortable: true, width: '140px' },
      { key: 'dob', header: 'DOB', accessor: (row) => row.dob || '-', searchValue: (row) => row.dob || '', sortable: true, dataType: 'date', width: '100px' },
      { key: 'bookingnumber', header: 'Booking', accessor: (row) => row.bookingnumber || '-', searchValue: (row) => row.bookingnumber || '', sortable: true, width: '90px' },
      {
        key: 'cid',
        header: 'CID',
        accessor: (row) => row.odrparticipant ? (
          <a className="text-[var(--azure-primary)] hover:underline hover:underline-offset-2" href={`/participants/${row.odrparticipant}`}>
            {row.cid || 'No CID'}
          </a>
        ) : <span title="Participant profile mapping is unavailable">{row.cid || 'No CID'}</span>,
        searchValue: (row) => row.cid || '',
        sortable: true,
        width: '90px',
      },
      { key: 'cin', header: 'CIN', accessor: (row) => row.cin || '-', searchValue: (row) => row.cin || '', sortable: true, width: '90px' },
      { key: 'program', header: 'Program', accessor: (row) => PROGRAM_LABELS[row.program] || row.program || '-', searchValue: (row) => row.program || '', sortable: true, width: '130px' },
      { key: 'facility', header: 'Facility', accessor: (row) => row.facility || '-', searchValue: (row) => row.facility || '', sortable: true, width: '140px' },
      { key: 'provider', header: 'Provider', accessor: (row) => row.provider || '-', searchValue: (row) => row.provider || '', sortable: true, width: '150px' },
      { key: 'hearingdate', header: 'Next Hearing Date', accessor: (row) => row.hearingdate || '-', searchValue: (row) => row.hearingdate || '', sortable: true, dataType: 'date', width: '120px' },
      { key: 'releasedon', header: 'Intake/Release Date', accessor: (row) => row.releasedon || '-', searchValue: (row) => row.releasedon || '', sortable: true, dataType: 'date', width: '125px' },
      { key: 'maxdate', header: 'Max Date', accessor: (row) => row.maxdate || '-', searchValue: (row) => row.maxdate || '', sortable: true, dataType: 'date', width: '105px' },
      { key: 'psh_search_status', header: 'PSH Search Status', accessor: (row) => row.psh_search_status || '-', searchValue: (row) => row.psh_search_status || '', sortable: true, width: '170px' },
      { key: 'lcm', header: 'ECM LCM', accessor: (row) => row.lcm || '-', searchValue: (row) => row.lcm || '', sortable: true, width: '130px' },
      {
        key: 'adherence_rate',
        header: 'Adherence Rate',
        accessor: (row) => {
          if (typeof row.adherence_rate !== 'number') {
            return '-';
          }

          const tone =
            row.adherence_rate > 66 ? 'cc-good' : row.adherence_rate < 33 ? 'cc-poor' : 'cc-mid';

          return <span className={`cc-adherence ${tone}`}>{row.adherence_rate}%</span>;
        },
        searchValue: (row) => row.adherence_rate || '',
        width: '130px',
      },
      {
        key: 'priority',
        header: 'Priority',
        accessor: (row) => (
          <PriorityEditor
            value={row.priority_id}
            disabled={isUpdatingPriority}
            onChange={(priorityId) => {
              void onPriorityEdit(row.participant, priorityId);
            }}
          />
        ),
        searchValue: (row) => row.priority || '',
        sortable: true,
        width: '120px',
      },
    ];
  }, [isUpdatingPriority, onPriorityEdit]);

  const canGoBack = currentPage > 1;
  const canGoNext = currentPage < pages;

  return (
    <div className="p-4 md:p-6">
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Case Conferencing Tool</h1>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          id="case-conferencing-refresh"
          className="h-8"
          onClick={() => {
            void refreshDashboard();
          }}
        >
          <RefreshCw className="h-4 w-4" />
          Refresh
        </Button>
      </div>

      <div className="mb-3 rounded-[var(--border-radius-medium)] border border-[var(--neutral-gray-40)] bg-white px-3 py-2.5">
        <div className="flex flex-wrap items-center gap-y-2 [&>div+div]:ml-3 [&>div+div]:border-l [&>div+div]:border-[var(--neutral-gray-70)] [&>div+div]:pl-3">
          <Input id="case-conferencing-firstname" className="w-36 h-8" aria-label="First name" placeholder="Firstname" value={filters.firstname} onChange={(event) => handleFilterChange('firstname', event.target.value)} />
          <Input id="case-conferencing-lastname" className="w-36 h-8" aria-label="Last name" placeholder="Lastname" value={filters.lastname} onChange={(event) => handleFilterChange('lastname', event.target.value)} />
          <Input id="case-conferencing-cid" className="w-36 h-8" aria-label="CID" placeholder="CID" value={filters.cid} onChange={(event) => handleFilterChange('cid', event.target.value)} />
          <CaseConferencingFilterPicker label="Program" values={filters.program} options={Object.entries(PROGRAM_LABELS).map(([value, label]) => ({ value, label }))} onChange={(values) => handleListFilterChange('program', values)} />
          <CaseConferencingFilterPicker label="Provider" values={filters.provider} options={providerOptions.map((value) => ({ value, label: value }))} onChange={(values) => handleListFilterChange('provider', values)} />
          <CaseConferencingFilterPicker label="Facility" values={filters.facility} options={facilityOptions.map((value) => ({ value, label: value }))} onChange={(values) => handleListFilterChange('facility', values)} />

          <div className="flex items-center gap-2">
            <div className="text-xs font-semibold uppercase tracking-wide text-[var(--neutral-gray-100)]">
              Priority
            </div>
            <select
              value={filters.priority}
              onChange={(event) => handleFilterChange('priority', event.target.value)}
              className="h-8 min-w-[105px] rounded-[var(--border-radius-medium)] border border-[var(--neutral-gray-60)] bg-white px-2 text-xs text-[var(--neutral-gray-120)]"
            >
              <option value="">All</option>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
          </div>
        </div>

        <div className={`mt-3 ${toolbarActionRowClass}`}>
          {hasActiveFilters && (
            <Button type="button" variant="outline" size="sm" onClick={clearFilters}>
              Clear Filters
            </Button>
          )}
          <Button type="button" variant="outline" size="sm" onClick={handleExportCsv} disabled={isExporting || filters !== debouncedFilters}>
            <span className="inline-flex items-center gap-1">
              <FileText className="h-3 w-3" aria-hidden="true" />
              <span>{isExporting ? 'Downloading...' : 'Report'}</span>
            </span>
          </Button>
        </div>
      </div>

      {effectiveError && <div role="alert" className="mb-2 text-sm text-[var(--semantic-error)]">{effectiveError}</div>}

      <UnifiedTable<CaseConferencingRow>
        columns={columns}
        data={rows}
        rowKey={getCaseConferencingRowKey}
        className="h-[66vh]"
        searchEnabled
        searchPlaceholder="Search case conferencing"
        loading={loading}
        emptyTitle="No case conferencing rows found"
        emptyDescription="Try changing filters."
        clientSort={false}
        sortKey={sortConfig.key || ''}
        sortDirection={sortConfig.direction || 'asc'}
        onSortChange={handleSortChange}
        stickyRowActions
        showRowActions
        renderRowActions={(row) => <Button id={`case-conferencing-details-${row.participant}`} variant="outline" size="sm" onClick={() => setDetailParticipant(row)}>Details</Button>}
        toolbarRight={
          <div className="flex flex-wrap items-center justify-end gap-2">
            <div className="flex items-center gap-1">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8 w-8 p-0"
                disabled={!canGoBack}
                onClick={() => {
                  if (!canGoBack) {
                    return;
                  }

                  setCurrentPage((previous) => Math.max(1, previous - 1));
                }}
                aria-label="Previous page"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="min-w-[80px] text-center text-xs text-[var(--neutral-gray-100)]">
                {currentPage} / {pages}
              </span>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8 w-8 p-0"
                disabled={!canGoNext}
                onClick={() => {
                  if (!canGoNext) {
                    return;
                  }

                  setCurrentPage((previous) => previous + 1);
                }}
                aria-label="Next page"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        }
      />
      {detailParticipant && (
        <CaseConferencingDetailsDialog
          participant={detailParticipant.participant}
          odrparticipant={detailParticipant.odrparticipant}
          name={`${detailParticipant.firstname} ${detailParticipant.lastname}`.trim()}
          onClose={() => setDetailParticipant(null)}
        />
      )}
    </div>
  );
};
